import { ActivityPackPayloadPatch, PackVocabulary } from './activity-pack';

/** O que a entrevista precisa descobrir. Espelha `ONBOARDING_SLOT` do backend. */
export enum ONBOARDING_SLOT {
  ACTIVITY = 'activity',
  BUSINESS_NAME = 'businessName',
  /** Se ele atende sozinho ou tem outros profissionais atendendo com ele. */
  TEAM = 'team',
  SERVICES = 'services',
  PRICING = 'pricing',
  DURATION = 'duration',
  GRID = 'grid',
  LOCATION = 'location',
  ADDRESS = 'address',
  /** Manual ou recebimento automático. Só a preferência; configura depois. */
  PAYMENTS = 'payments',
  RECURRENCE = 'recurrence',
  CAPACITY = 'capacity',
}

/** Espelha `PAYMENT_PREFERENCE` do backend. */
export type PaymentPreference = 'manual' | 'automatic';

export enum ONBOARDING_DRAFT_STATUS {
  IN_PROGRESS = 'IN_PROGRESS',
  /** Todos os slots obrigatórios preenchidos: o app mostra o resumo. */
  READY = 'READY',
  /** O apply rodou: virou serviço, grade e regras de verdade. */
  APPLIED = 'APPLIED',
  ABANDONED = 'ABANDONED',
}

/**
 * Os cartões que a conversa desenha. Fechado em enum porque o backend deriva
 * o widget do rascunho — o modelo nunca gera interface. Tipo que não estiver
 * aqui é ignorado em silêncio pelo renderer.
 */
export enum INTERVIEW_WIDGET {
  WEEKLY_GRID = 'WEEKLY_GRID',
  SERVICE_LIST = 'SERVICE_LIST',
  SUBSCRIPTION_CARD = 'SUBSCRIPTION_CARD',
  LOCATION = 'LOCATION',
  CLIENT_PREVIEW = 'CLIENT_PREVIEW',
  /** O roteiro fixo: pergunta de múltipla escolha, respondida por toque. */
  CHOICE = 'CHOICE',
  /** O roteiro fixo: marcar serviços do ramo — no passo e nas sugestões. */
  SERVICE_PICKER = 'SERVICE_PICKER',
  /** O roteiro fixo: o endereço de quem recebe cliente, buscado por CEP. */
  ADDRESS = 'ADDRESS',
  /** O roteiro fixo: uma resposta curta de uma linha (o nome do negócio). */
  TEXT = 'TEXT',
  /** Depois do resumo: qual parte ele quer ajustar. */
  ADJUST_MENU = 'ADJUST_MENU',
  /** Fase 2 — o cartão Confirmar/Cancelar do assistente (T-32). */
  CONFIRMATION = 'CONFIRMATION',
  /** Fase 2 — o atalho para a tela certa quando o pedido é fora da fronteira. */
  SHORTCUT = 'SHORTCUT',
  /** Fase 2 — cartão de leitura genérico: título + linhas rótulo/valor. */
  INFO_LIST = 'INFO_LIST',
}

export interface InterviewWidget {
  type: INTERVIEW_WIDGET;
  data: Record<string, unknown>;
}

/** `GET /activity-packs/interview/status` */
export interface InterviewStatus {
  /** Falso: o app troca a conversa pela lista de ramos (T-24). */
  agentAvailable: boolean;
  model: string | null;
  maxTurns: number;
  /** Falso: pedir o aceite do envio à IA de terceiros antes da entrevista. */
  aiConsent: boolean;
}

/** `POST /activity-packs/interview/message` */
export interface InterviewTurn {
  message: string;
  /**
   * O que o toque no cartão "disse": vira bolha do prestador antes da fala do
   * agente. Sem isto o chat fica com um lado só, porque ele responde tudo
   * tocando.
   */
  echo?: string | null;
  widget: InterviewWidget | null;
  packSlug: string | null;
  activityLabel: string | null;
  status: ONBOARDING_DRAFT_STATUS;
  filledSlots: ONBOARDING_SLOT[];
  pendingSlots: ONBOARDING_SLOT[];
  turnCount: number;
  turnsLeft: number;
  /** Rascunho pronto: troque a conversa pelo resumo. */
  finished: boolean;
  /** Acabou sem fechar: ofereça a lista de ramos. */
  exhausted: boolean;
}

/** Uma fala guardada no rascunho — é com isto que o chat é remontado. */
export interface InterviewTurnRecord {
  role: 'agent' | 'provider';
  text: string;
  widget?: InterviewWidget | null;
  /** ISO-8601. */
  at: string;
}

/** `GET /activity-packs/interview/draft` */
export interface OnboardingDraft {
  packSlug: string | null;
  status: ONBOARDING_DRAFT_STATUS;
  payload: ActivityPackPayloadPatch;
  filledSlots: ONBOARDING_SLOT[];
  pendingSlots: ONBOARDING_SLOT[];
  turnCount: number;
  messages: InterviewTurnRecord[];
}

export interface DraftServiceSummary {
  name: string;
  /** "R$ 45", "a partir de R$ 90", "a combinar". */
  price: string;
  /** "45 min", "1h30", "duração combinada". */
  duration: string;
  /** "Plano fixo: R$ 320 por mês". Nulo quando não há — a linha some. */
  recurrence: string | null;
  /** "até 8 pessoas no mesmo horário". Nulo em atendimento individual. */
  capacity: string | null;
}

/**
 * `GET /activity-packs/interview/preview` — o resumo de confirmação já em
 * linguagem de negócio. A tradução mora no backend: o app não formata nada.
 */
export interface DraftPreview {
  packSlug: string | null;
  activityLabel: string | null;
  status: ONBOARDING_DRAFT_STATUS;
  /** Vazio = dá para confirmar. */
  pendingSlots: ONBOARDING_SLOT[];
  services: DraftServiceSummary[];
  /** "Terça a sexta, 9h às 19h". */
  scheduleLines: string[];
  locationLine: string | null;
  /** "Você e mais profissionais atendendo". Nulo quando é ele sozinho. */
  teamLine: string | null;
  /** Manual ou automático: decide o fim do cadastro. */
  paymentPreference: PaymentPreference | null;
  /** "Recebimento automático (você ativa logo depois do cadastro)". */
  paymentLine: string | null;
  vocabulary: Partial<PackVocabulary> | null;
}

/** O que o apply criou de verdade — zero em tudo é reaplicação. */
export interface ApplyResult {
  servicesCreated: number;
  availabilityCreated: number;
  contractTemplatesCreated: number;
  scheduleSettingsCreated: boolean;
  preview: DraftPreview;
}

/** O corpo do `POST /interview/adjust`: qual parte ajustar, ou encerrar. */
export interface AdjustInput {
  slot?: ONBOARDING_SLOT;
  done?: boolean;
}

/** O corpo do `PATCH /draft`: a edição por widget, sem passar pelo modelo. */
export interface PatchDraftInput {
  packSlug?: string;
  payload?: ActivityPackPayloadPatch;
  /** Nomes de serviço a remover — desmarcar no cartão. */
  removeServices?: string[];
}
